import { createApi } from '@reduxjs/toolkit/query/react'
import { THERAPIST_LOOKUP, HOME_EDUCATION_SERVICE_LIST, PARENT_LOOKUP, STATUS_LIST, TEACHER_LOOKUP, UPLOAD_FILE, LESSONS_LIST, FUNDING_SOURCE_UTILITY_LIST, PROGRAM_LOOKUP, MDT_CHATTER_STATUS, MDT_TERMS_LIST, MDT_THERAPIST_STATUS, ASSOCIATED_CLIENT_LOOKUP, CLIENT_LOOKUP, ROLES_LOOKUP, CLIENT_SERVICE_STATUS_LOOKUP, REPORT_OPTIONS, SCHOOL_LOOKUP, AUDIT_LOGS_LIST, AUDIT_LOGS_EVENT_TYPE_LIST, SUBADMIN_LOOKUP, SUPERADMIN_LOOKUP, COORDINATOR_LOOKUP, APPRENTICE_LOOKUP_V2 } from "config/apiPaths"
import axiosBaseQuery from "utils/axios/axiosBaseQuery"

export const utilityApi = createApi({
  reducerPath: 'utilityApi',
  baseQuery: axiosBaseQuery({
    baseUrl: "/utility",
  }),
  tagTypes: ["TeacherLookup", "ParentLookup", "StatusList", "TherapistLookup", "LessonsLookup", "FundingSource", "ProgramLookup", "MdtChatterStatus", "MdtTerms", "MdtTherapistStatus", "ClientServiceStatusLookup"],
  endpoints: (builder) => ({

    // upload file
    uploadFile: builder.mutation({
      query: ({ file, acl, signal, onUploadProgress }) => {
        // Ensure acl and file are passed in
        if (!acl) {
          throw new Error("acl is required");
        }
        if (!file) {
          throw new Error("file not provided to upload");
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("acl", acl);

        return {
          url: UPLOAD_FILE,
          method: 'POST',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          signal,
          onUploadProgress
        };
      },
      transformResponse: (response) => {
        return response?.data;
      }
    }),

    // teachers lookup
    teacherLookup: builder.query({
      query: ({search, schoolId}) => ({
        method: "GET",
        url: `${TEACHER_LOOKUP}${search ? `/${search}` : ""}${schoolId ? `?school_id=${schoolId}` : ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["TeacherLookup"]
    }),

    // coordinators lookup
    coordinatorLookup: builder.query({
      query: (search) => ({
        method: "GET",
        url: `${COORDINATOR_LOOKUP}${search ? `/${search}` : ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["CoordinatorLookup"]
    }),

    // parents lookup
    parentLookup: builder.query({
      query: (search) => ({
        method: "GET",
        url: `${PARENT_LOOKUP}${search ? `/${search}` : ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["ParentLookup"]
    }),

    // therapist lookup
    therapistLookup: builder.query({
      query: ({ skip_test, therapist_type = "", notes_collab = "" } = {}) => ({
        method: "GET",
        url: `${THERAPIST_LOOKUP}${skip_test ? "?skip_test=true" : ""}${notes_collab ? "&notes_collab=true" : ""}`,
        params: { therapist_type }
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["TherapistLookup"]
    }),

    // statues list
    getStatusList: builder.query({
      query: () => ({
        method: "GET",
        url: `${STATUS_LIST}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["StatusList"]
    }),

    // home education service list
    getHomeEducationServiceList: builder.query({
      query: () => ({
        method: "GET",
        url: `${HOME_EDUCATION_SERVICE_LIST}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["StatusList"]
    }),

    // lessons list
    getLessonsList: builder.query({
      query: ({ search = "", tag_id = [], page, limit, sort = { column: "id", direction: "DESC" } } = {}) => ({
        method: "POST",
        url: `${LESSONS_LIST}`,
        data: { search, tag_id, page, limit, sort }
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["LessonsLookup"]
    }),

    // funding source list
    getFundingSourceList: builder.query({
      query: () => ({
        method: "GET",
        url: `${FUNDING_SOURCE_UTILITY_LIST}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["FundingSource"]
    }),

    // funding source list
    programLookup: builder.query({
      query: (search) => ({
        method: "GET",
        url: `${PROGRAM_LOOKUP}${search ? `/${search}` : ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["ProgramLookup"]
    }),

    // MDT chatter status list
    getMdtChatterStatus: builder.query({
      query: () => ({
        method: "GET",
        url: MDT_CHATTER_STATUS
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["MdtChatterStatus"]
    }),

    // MDT terms
    getMdtTermsList: builder.query({
      query: () => ({
        method: "GET",
        url: MDT_TERMS_LIST
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["MdtTerms"]
    }),

    // MDT Check list status
    getMdtTermsList: builder.query({
      query: () => ({
        method: "GET",
        url: MDT_TERMS_LIST
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["MdtTerms"]
    }),

    // MDT therapist status
    getMdtTherapistStatusList: builder.query({
      query: () => ({
        method: "GET",
        url: MDT_THERAPIST_STATUS
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["MdtTherapistStatus"]
    }),

    // Get file url
    getFileUrl: builder.query({
      query: (key) => ({
        method: "GET",
        url: `${UPLOAD_FILE}/${key}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
    }),

    // Get associated clients
    getAssociatedClients: builder.query({
      query: ({ search, client_type }) => ({
        method: "GET",
        url: `${ASSOCIATED_CLIENT_LOOKUP}/${client_type ? `${client_type + "/"}` : ""}lookup${search ? `/${search}` : ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
    }),

    //search with grade
    getAssociatedClientsWithGrade: builder.query({
      query: ({ search, client_type, school, grade }) => ({
        method: "GET",
        url: `${ASSOCIATED_CLIENT_LOOKUP}/${client_type ? `${client_type + "/"}` : ""}${school ? `${school + "/"}` : ""}${grade ? `${grade + "/"}` : ""}lookup${search ? `/${search}` : ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
    }),

    // client lookup
    clientLookupSearch: builder.query({
      query: ({ search, client }) => ({
        method: "GET",
        url: `${client == "peer" ? "/peer" : CLIENT_LOOKUP}/${client}/lookup/${search}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
    }),

    // roles lookup
    rolesLookup: builder.query({
      query: () => ({
        method: "GET",
        url: ROLES_LOOKUP
      }),
      transformResponse: (response) => {
        return response?.data;
      },
    }),

    // Clients service status list 
    clientsServiceStatusList: builder.query({
      query: ({ module, client_type }) => ({
        method: "GET",
        url: `${CLIENT_SERVICE_STATUS_LOOKUP}/${module}/${client_type}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["ClientServiceStatusLookup"]
    }),

    getReportOptions: builder.query({
      query: () => ({
        method: "GET",
        url: REPORT_OPTIONS
      }),
      transformResponse: (response) => {
        return response?.data;
      },
    }),

    // school lookup
    schoolLookup: builder.query({
      query: (search) => ({
        method: "GET",
        url: `${SCHOOL_LOOKUP}${search ? `/${search}` : ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["SchoolLookup"]
    }),

    // Audit logs
    auditLogsList: builder.query({
      query: (filters) => ({
        method: "PATCH",
        url: AUDIT_LOGS_LIST,
        data: filters,
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["AuditLogsList"]
    }),

    // Audit logs event types
    auditLogsEventTypeList: builder.query({
      query: (params) => ({
        method: "GET",
        url: AUDIT_LOGS_EVENT_TYPE_LIST,
        params
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["AuditLogsEventTypeList"]
    }),

    // sub-admin lookup
    subAdminLookup: builder.query({
      query: ({ search, skip_test, notes_collab = "" }) => ({
        method: "GET",
        url: `${SUBADMIN_LOOKUP}${search ? `/${search}` : ""}${skip_test ? "?skip_test=true" : ""}${notes_collab ? "&notes_collab=true" : ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["SubAdminLookup"]
    }),

    // super-admin lookup
    superAdminLookup: builder.query({
      query: ({ search, skip_test }) => ({
        method: "GET",
        url: `${SUPERADMIN_LOOKUP}${search ? `/${search}` : ""}${skip_test ? "?skip_test=true" : ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["SuperAdminLookup"]
    }),

    apprenticeLookup: builder.query({
      query: ({search, disciplineId}) => ({
        method: "GET",
        url: `${APPRENTICE_LOOKUP_V2}${search ? `/${search}` : ""}${disciplineId ? `?discipline_id=${disciplineId}` : ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      }
    }),

  }),
})

export const {
  useUploadFileMutation,
  useTeacherLookupQuery,
  useParentLookupQuery,
  useGetStatusListQuery,
  useGetHomeEducationServiceListQuery,
  useTherapistLookupQuery,
  useGetLessonsListQuery,
  useGetFundingSourceListQuery,
  useProgramLookupQuery,
  useGetMdtChatterStatusQuery,
  useGetMdtTermsListQuery,
  useGetMdtTherapistStatusListQuery,
  useLazyGetFileUrlQuery,
  useLazyGetAssociatedClientsQuery,
  useLazyGetAssociatedClientsWithGradeQuery,
  useLazyParentLookupQuery,
  useLazyTeacherLookupQuery,
  useLazyCoordinatorLookupQuery,
  useLazyProgramLookupQuery,
  useLazyClientLookupSearchQuery,
  useRolesLookupQuery,
  useClientsServiceStatusListQuery,
  useGetReportOptionsQuery,
  useSchoolLookupQuery,
  useAuditLogsListQuery,
  useAuditLogsEventTypeListQuery,
  useLazyTherapistLookupQuery,
  useSubAdminLookupQuery,
  useSuperAdminLookupQuery,
  useApprenticeLookupQuery,
  useLazyApprenticeLookupQuery
} = utilityApi;